import AddIcon from '@mui/icons-material/Add';
import DownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Button, Stack } from '@mui/material';
import { useAtomValue } from 'jotai';
import { ReactElement } from 'react';
import { useEntityManagementEnabled } from '../hooks/useEntityManagementEnabled';
import { entityManagementHeaderActionsAtom } from '../state/EntityManagementState';

/**
 * Entity Management header actions (FD-4464): `+ Add entity` · `+ Holding` · `Guide` ·
 * `Export`.
 *
 * Rendered into the app header's children slot from NavWrapper, so it shares the row
 * with the app's breadcrumb — matching the prototype and every other module, rather
 * than adding a second header bar below the first.
 *
 * Handlers are registered per-surface via `entityManagementHeaderActionsAtom`;
 * unregistered actions do not render, which is what keeps them hidden until the
 * surface tickets wire them (FD-4470 / FD-4471 / FD-4472).
 */
export function EntityManagementHeaderActions(): ReactElement | null {
  const isEnabled = useEntityManagementEnabled();
  const { onAddEntity, onAddHolding, onExport, onOpenGuide } = useAtomValue(
    entityManagementHeaderActionsAtom
  );

  // Nothing to show: either the module is gated off (the route renders Forbidden) or
  // no surface has registered an action yet. Returning null rather than an empty stack
  // keeps the header row from reserving space for it.
  if (!isEnabled || (!onAddEntity && !onAddHolding && !onExport && !onOpenGuide)) {
    return null;
  }

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='flex-end'
      gap={1}
      data-testid='entity-management-header-actions'
    >
      {onAddEntity && (
        <Button
          variant='outlined'
          size='small'
          color='secondary'
          startIcon={<AddIcon fontSize='small' />}
          onClick={onAddEntity}
        >
          Add entity
        </Button>
      )}
      {onAddHolding && (
        <Button
          variant='outlined'
          size='small'
          color='secondary'
          startIcon={<AddIcon fontSize='small' />}
          onClick={onAddHolding}
        >
          Holding
        </Button>
      )}
      {onOpenGuide && (
        <Button
          variant='text'
          size='small'
          color='secondary'
          startIcon={<HelpOutlineOutlinedIcon fontSize='small' />}
          onClick={onOpenGuide}
        >
          Guide
        </Button>
      )}
      {onExport && (
        <Button
          variant='outlined'
          size='small'
          color='secondary'
          startIcon={<DownloadOutlinedIcon fontSize='small' />}
          // The button is the menu's anchor, so the surface gets it rather than the
          // shell knowing what the menu contains.
          onClick={(event) => onExport(event.currentTarget)}
        >
          Export
        </Button>
      )}
    </Stack>
  );
}
